import React, { useEffect, useState } from 'react';
import { Button, Drawer, Dropdown, Menu } from 'antd';
import { CloseOutlined, DownOutlined, LeftOutlined, MenuOutlined, PhoneOutlined, RightOutlined } from '@ant-design/icons';
import styles from './styles.module.css';
import { useNavigate } from 'react-router-dom';

type MenuLevel = 'root' | 'practices' | 'about';

const practiceLinks = [
  { label: 'Corporate & Commercial Transactions', path: '/practices/corporate-transactions' },
  { label: 'Mergers & Acquisitions', path: '/practices/mergers-acquisitions' },
  { label: 'Business Formation & Governance', path: '/practices/business-formation' },
  { label: 'Contracts & Negotiations', path: '/practices/contracts' },
  { label: 'Cross-Border & International Business', path: '/practices/cross-border' },
  { label: 'Private Investments & Financing', path: '/practices/investments' },
  { label: 'Regulatory & Compliance Support', path: '/practices/compliance' },
];

const aboutLinks = [
  { label: 'The Firm', path: '/about/firm' },
  { label: 'Attorney Profile', path: '/about/attorney' },
  { label: 'Our Approach', path: '/about/approach' },
];

function LawFirmHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuLevel, setMenuLevel] = useState<MenuLevel>('root');
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
    setMenuLevel('root');
  };

  const handleScheduleConsultation = () => handleNavigation('/contact');

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    // slight delay so the closing animation doesn't flash the root menu
    setTimeout(() => setMenuLevel('root'), 300);
  };

  // ── Desktop dropdown menus ──────────────────────────────────────────────────

  const practicesMenu = (
    <Menu>
      {practiceLinks.map(({ label, path }) => (
        <Menu.Item key={path}>
          <a href={path}>{label}</a>
        </Menu.Item>
      ))}
    </Menu>
  );

  const aboutMenu = (
    <Menu>
      {aboutLinks.map(({ label, path }) => (
        <Menu.Item key={path}>
          <a href={path}>{label}</a>
        </Menu.Item>
      ))}
    </Menu>
  );

  // ── Mobile Drawer content ───────────────────────────────────────────────────

  const drawerTitle = (
    <div className={styles.drawerHeader}>
      {menuLevel !== 'root' ? (
        <Button
          type="text"
          icon={<LeftOutlined />}
          className={styles.drawerBackBtn}
          onClick={() => setMenuLevel('root')}
        >
          Back
        </Button>
      ) : (
        <span className={styles.drawerTitle}>Menu</span>
      )}
      <Button
        type="text"
        icon={<CloseOutlined />}
        className={styles.drawerCloseBtn}
        onClick={handleDrawerClose}
      />
    </div>
  );

  const rootMenu = (
    <nav className={styles.drawerNav}>
      <button className={styles.drawerItem} onClick={() => handleNavigation('/')}>
        Home
      </button>
      <button className={styles.drawerItem} onClick={() => setMenuLevel('practices')}>
        Practice Areas
        <RightOutlined className={styles.drawerArrow} />
      </button>
      <button className={styles.drawerItem} onClick={() => setMenuLevel('about')}>
        About
        <RightOutlined className={styles.drawerArrow} />
      </button>
      <button className={styles.drawerItem} onClick={() => handleNavigation('/contact')}>
        Contact
      </button>
      <div className={styles.drawerDivider} />
      <button
        className={`${styles.drawerItem} ${styles.drawerCta}`}
        onClick={handleScheduleConsultation}
      >
        <PhoneOutlined style={{ marginRight: 8 }} />
        Schedule Consultation
      </button>
    </nav>
  );

  const subMenu = (links: typeof practiceLinks, heading: string) => (
    <nav className={styles.drawerNav}>
      <p className={styles.drawerSubheading}>{heading}</p>
      {links.map(({ label, path }) => (
        <button
          key={path}
          className={styles.drawerItem}
          onClick={() => handleNavigation(path)}
        >
          {label}
        </button>
      ))}
    </nav>
  );

  const drawerContent = () => {
    if (menuLevel === 'practices') return subMenu(practiceLinks, 'Practice Areas');
    if (menuLevel === 'about') return subMenu(aboutLinks, 'About');
    return rootMenu;
  };

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <header className={`${styles.header} ${scrolled ? styles.scrolled : ''}`}>
      {/* Left: Desktop navigation */}
      <div className={styles.left}>
        <nav className={styles.nav}>
          <a href="/" className={styles.navItem}>Home</a>

          <Dropdown overlay={practicesMenu} placement="bottom">
            <Button type="text" className={`${styles.navItem} ${styles.dropdownButton}`}>
              Practices <DownOutlined style={{ fontSize: '12px', marginLeft: '4px' }} />
            </Button>
          </Dropdown>

          <Dropdown overlay={aboutMenu} placement="bottom">
            <Button type="text" className={`${styles.navItem} ${styles.dropdownButton}`}>
              About <DownOutlined style={{ fontSize: '12px', marginLeft: '4px' }} />
            </Button>
          </Dropdown>

          <a href="/contact" className={styles.navItem}>Contact</a>
        </nav>
      </div>

      {/* Center: Logo */}
      <div className={styles.center}>
        <div className={styles.logo}>
          <span className={styles.firmName}>Maiskii & Associates</span>
          <span className={styles.tagline}>Transactional Counsel</span>
        </div>
      </div>

      {/* Right: CTA + mobile hamburger */}
      <div className={styles.right}>
        <Button
          type="primary"
          icon={<PhoneOutlined />}
          className={styles.ctaButton}
          onClick={handleScheduleConsultation}
        >
          Schedule Consultation
        </Button>

        <Button
          type="text"
          icon={<MenuOutlined />}
          className={styles.mobileMenu}
          onClick={() => setDrawerOpen(true)}
          aria-label="Open navigation menu"
        />
      </div>

      {/* Mobile Drawer */}
      <Drawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        placement="right"
        width={300}
        closable={false}
        title={drawerTitle}
        className={styles.mobileDrawer}
        styles={{
          header: { padding: 0, border: 'none' },
          body: { padding: 0 },
        }}
      >
        {drawerContent()}
      </Drawer>
    </header>
  );
}

export default LawFirmHeader;
